import { Button, Popover, Switch, Typography } from '@mui/material';
import {
  LocationOnOutlined,
  Numbers,
  PersonOutline,
} from '@mui/icons-material';
import { Stack } from '@mui/system';
import {
  FunctionComponent,
  useEffect,
  useCallback,
  useRef,
  useState,
} from 'react';
import {
  EngagePostMessageActions,
  inboxFiltersService,
  InboxFiltersObject,
  locationsService,
  phoneNumbersService,
  SendMessage,
  useIframeEngageActionListener,
  User,
  usersService,
} from '../../services';
import {
  ApplyFilterButton,
  ButtonsContainer,
  SwitchContainer,
  TextFieldContainer,
  Title,
} from './filter-inbox.styles';
import { AutocompleteField } from '../autocomplete-field';

interface FiltersMetaData {
  locations: FilterMetaData[];
  salesReps: FilterMetaData[];
  assignedUsers: FilterMetaData[];
  phoneNumbers: FilterMetaData[];
}

type FilterMetaData = Pick<User, 'name' | 'id'>;

interface FilterInboxProps {
  accessToken: string;
  api1Origin: string;
  engageOrigin: string;
}

type filterType =
  | 'engage-number'
  | 'location'
  | 'sales-rep'
  | 'assigned-to-others'
  | 'is-read';

const sameIds = (a: (string | number)[], b: (string | number)[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

// Compares against the last-applied filters, not just "is anything selected" —
// removing the last active filter is a real change that Apply must allow, even
// though the resulting payload is empty.
const filtersMatch = (a: InboxFiltersObject, b: InboxFiltersObject) =>
  sameIds(a.locationIds, b.locationIds) &&
  sameIds(a.salesRepIds, b.salesRepIds) &&
  sameIds(a.phoneNumbers, b.phoneNumbers) &&
  sameIds(a.assignedUserIds, b.assignedUserIds) &&
  a.isRead === b.isRead;

export const FilterInbox: FunctionComponent<FilterInboxProps> = ({
  accessToken,
  api1Origin,
  engageOrigin,
}) => {
  const eventBody = useIframeEngageActionListener({
    engageOrigin,
    action: EngagePostMessageActions.OPEN_INBOX_FILTER,
  });
  const refreshEventBody = useIframeEngageActionListener({
    engageOrigin,
    action: EngagePostMessageActions.REFRESH_INBOX_FILTER,
  });
  const [open, setOpen] = useState(false);
  const [clearFiltersDisabled, setClearFiltersDisabled] = useState(false);
  const [targetPosition, setTargetPosition] = useState<null | {
    left: number;
    top: number;
  }>(null);
  const [applyFiltersDisabled, setApplyFiltersDisabled] = useState(true);

  const [filtersPayload, setFiltersPayload] = useState(
    inboxFiltersService.defaultInboxFiltersObject
  );
  const appliedFiltersRef = useRef(filtersPayload);

  const [locations, setLocations] = useState<User[]>([]);
  const [assignToOptions, setAssignToOptions] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [salesRepOptions, setSalesRepOptions] = useState<User[]>([]);
  const [phoneNumberOptions, setPhoneNumberOptions] = useState<User[]>([]);

  const sendFiltersWithNames = useCallback(
    (filters) => {
      const payload = {
        REQUEST_ACTION: {
          type: 'POST_CONVERSATION_FILTERS',
          filtersPayload: filters,
        },
      };
      SendMessage(payload, engageOrigin, true);
    },
    [engageOrigin]
  );

  const getFiltersWithNames = useCallback(
    (filters): FiltersMetaData => {
      const selectedFilters: FiltersMetaData = {
        locations: locations
          .filter(({ id }) => filters?.locationIds?.includes(id))
          .map(({ jnid, id, name }) =>
            jnid ? { id: jnid, name } : { id, name }
          ) as FilterMetaData[],
        salesReps: salesRepOptions
          .filter(({ jnid }) => filters?.salesRepIds?.includes(jnid))
          .map(({ jnid, id, name }) =>
            jnid ? { id: jnid, name } : { id, name }
          ) as FilterMetaData[],
        assignedUsers: assignToOptions
          .filter(({ jnid }) => filters?.assignedUserIds?.includes(jnid))
          .map(({ jnid, id, name }) =>
            jnid ? { id: jnid, name } : { id, name }
          ) as FilterMetaData[],
        phoneNumbers: phoneNumberOptions
          .filter(({ jnid }) => filters?.phoneNumbers?.includes(jnid))
          .map(({ jnid, name }) => ({ id: jnid, name })) as FilterMetaData[],
      };
      return selectedFilters;
    },
    [assignToOptions, locations, phoneNumberOptions, salesRepOptions]
  );

  useEffect(() => {
    setOpen(eventBody != null);

    if (eventBody) {
      setTargetPosition(eventBody.targetPosition);
    }
  }, [eventBody, accessToken, api1Origin, engageOrigin]);

  useEffect(() => {
    if (refreshEventBody) {
      clearInputFields();
      fetchCurrentFilters(engageOrigin, accessToken);
    }
  }, [refreshEventBody, accessToken, engageOrigin]);

  useEffect(() => {
    if (!loading) return;

    fetchCurrentFilters(engageOrigin, accessToken);
    setUsersFromService();
    setPhoneNumberOptionsFromService(engageOrigin, accessToken);
    fetchLocations(api1Origin, accessToken);
    setLoading(false);
  }, [accessToken, api1Origin, engageOrigin, locations, loading]);

  const setUsersFromService = async () => {
    const response = await usersService.getActiveUsers(accessToken, api1Origin);

    setAssignToOptions(response);
    setSalesRepOptions(response);
  };

  const setPhoneNumberOptionsFromService = async (
    engageOrigin,
    accessToken
  ) => {
    const response = (await phoneNumbersService.getPhoneNumbers(
      engageOrigin,
      accessToken
    )) as User[];

    setPhoneNumberOptions(response);
  };

  const fetchLocations = async (api1Origin, accessToken) => {
    const response = await locationsService.getLocations(
      api1Origin,
      accessToken
    );

    setLocations(response);
  };

  const fetchCurrentFilters = async (engageOrigin, accessToken) => {
    const filters = await inboxFiltersService.getCurrentFilters(
      engageOrigin,
      accessToken
    );

    setFiltersPayload(filters);
    appliedFiltersRef.current = filters;

    const filtersDisabled = !(
      !!filters.assignedUserIds.length ||
      !!filters.locationIds.length ||
      !!filters.phoneNumbers.length ||
      !!filters.salesRepIds.length ||
      filters.isRead !== undefined
    );

    setApplyFiltersDisabled(filtersDisabled);
    setClearFiltersDisabled(filtersDisabled);
  };

  //   Call this on any input onchange event to update filter payload
  const updateFiltersPayload = (
    filter: filterType,
    ids: (string | number)[] | boolean
  ) => {
    const updatedFilters = Object.assign({}, filtersPayload);

    switch (filter) {
      case 'assigned-to-others':
        updatedFilters.assignedUserIds = ids as string[];
        break;
      case 'location':
        updatedFilters.locationIds = ids as number[];
        break;
      case 'engage-number':
        updatedFilters.phoneNumbers = ids as string[];
        break;
      case 'sales-rep':
        updatedFilters.salesRepIds = ids as string[];
        break;
      case 'is-read':
        if (ids) {
          updatedFilters.isRead = false;
        } else {
          delete updatedFilters.isRead;
        }
        break;
    }

    setFiltersPayload(updatedFilters);

    const filtersAreEmpty = !(
      !!updatedFilters.assignedUserIds.length ||
      !!updatedFilters.locationIds.length ||
      !!updatedFilters.phoneNumbers.length ||
      !!updatedFilters.salesRepIds.length ||
      updatedFilters.isRead !== undefined
    );

    setApplyFiltersDisabled(filtersMatch(updatedFilters, appliedFiltersRef.current));
    setClearFiltersDisabled(filtersAreEmpty);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const clearFilters = async () => {
    const response = await inboxFiltersService.deleteFilters(
      engageOrigin,
      accessToken
    );

    if (response === 'success') {
      appliedFiltersRef.current = inboxFiltersService.defaultInboxFiltersObject;
      clearInputFields();
      setClearFiltersDisabled(true);
      setApplyFiltersDisabled(true);
      sendUpdateFiltersMessage();
      handleClose();

      return;
    }

    console.log('error deleting filters: ', response);
  };

  const sendUpdateFiltersMessage = () => {
    const payload = {
      REQUEST_ACTION: {
        type: 'UPDATE_CONVERSATION_FILTERS',
      },
    };
    SendMessage(payload, engageOrigin, true);
  };

  const applyFilters = async () => {
    const response = await inboxFiltersService.saveFilters(
      engageOrigin,
      accessToken,
      filtersPayload
    );

    if (response) {
      appliedFiltersRef.current = filtersPayload;
      const filtersWithNames = getFiltersWithNames(filtersPayload);
      sendFiltersWithNames(filtersWithNames);

      sendUpdateFiltersMessage();
      handleClose();

      return;
    }

    console.log('error saving filters: ', response);
  };

  const clearInputFields = () => {
    setFiltersPayload(inboxFiltersService.defaultInboxFiltersObject);
  };

  return (
    <div>
      <Popover
        id="inbox-filters-popover"
        data-e2e="inbox-filters-popover"
        open={open}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={targetPosition}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{ sx: { width: '528px' } }}
      >
        <Title>
          <Typography marginTop={2}>Filter</Typography>
          <Button
            data-e2e="clear-inbox-filters"
            disabled={clearFiltersDisabled}
            onClick={clearFilters}
          >
            Clear Filters
          </Button>
        </Title>
        <Stack>
          {locations?.length >= 2 && (
            <TextFieldContainer key="locationContainer">
              <Typography>Location</Typography>
              <AutocompleteField
                filterType="location"
                icon={LocationOnOutlined}
                options={locations}
                value={locations.filter(({ id }) =>
                  filtersPayload.locationIds.some((lId) => lId === id)
                )}
                updateFilters={updateFiltersPayload}
              ></AutocompleteField>
            </TextFieldContainer>
          )}
          <TextFieldContainer>
            <Typography>Assigned to others</Typography>
            <AutocompleteField
              filterType="assigned-to-others"
              icon={PersonOutline}
              options={assignToOptions}
              value={assignToOptions.filter((user) =>
                filtersPayload.assignedUserIds.includes(user.jnid)
              )}
              updateFilters={updateFiltersPayload}
            ></AutocompleteField>
          </TextFieldContainer>
          <TextFieldContainer key="salesRepContainer">
            <Typography>Sales Rep</Typography>
            <AutocompleteField
              filterType="sales-rep"
              icon={PersonOutline}
              options={salesRepOptions}
              value={salesRepOptions.filter((user) =>
                filtersPayload.salesRepIds.includes(user.jnid)
              )}
              updateFilters={updateFiltersPayload}
            ></AutocompleteField>
          </TextFieldContainer>
          <TextFieldContainer key="phoneNumberContainer">
            <Typography>Engage number</Typography>
            <AutocompleteField
              filterType="engage-number"
              icon={Numbers}
              options={phoneNumberOptions}
              value={phoneNumberOptions.filter(({ jnid }) =>
                filtersPayload.phoneNumbers.includes(jnid)
              )}
              updateFilters={updateFiltersPayload}
            ></AutocompleteField>
          </TextFieldContainer>
          <SwitchContainer data-e2e="is-read-inbox-filter">
            <Typography>Show unread only</Typography>
            <Switch
              data-e2e="is-read-inbox-filter-switch"
              inputProps={{ 'aria-label': 'Show unread only' }}
              checked={filtersPayload.isRead === false}
              onChange={(event) =>
                updateFiltersPayload('is-read', event.target.checked)
              }
            />
          </SwitchContainer>
        </Stack>
        <ButtonsContainer>
          <Button data-e2e="cancel-inbox-filters" onClick={handleClose}>
            Cancel
          </Button>
          <ApplyFilterButton
            data-e2e="apply-inbox-filters"
            disabled={applyFiltersDisabled}
            onClick={applyFilters}
          >
            Apply
          </ApplyFilterButton>
        </ButtonsContainer>
      </Popover>
    </div>
  );
};
